import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

const conditionSchema = z.object({
  field: z
    .string()
    .describe(
      "Column name in camelCase (e.g., 'supplierContractNo', 'invoicedAmount')",
    ),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
    "in",
    "not_in",
    "regex_match",
  ]),
  value: z.unknown().optional(),
});

/**
 * Classify Master Data Step
 *
 * Evaluates configurable rules against rows in a typed master data table
 * and assigns statuses. Reuses the same rule engine as pipeline_rows_process
 * but works directly on typed model columns instead of JSONB row_data.
 *
 * Use cases:
 * - Classify E844SD rows by contract type (internal vs external)
 * - Filter zero-amount or credit memo rows
 * - Route rows to different processing buckets by status
 *
 * Example workflow:
 * ```
 * Schedule Trigger → Blob Fetch → Parse → Bulk Insert → Classify Master Data
 *     Rules: [
 *       { conditions: [{field: "supplierContractNo", operator: "starts_with", value: "*"}],
 *         action: {setStatus: "staged_internal"} },
 *     ]
 * ```
 */
export const classifyMasterDataStep = defineStepConfig({
  type: "classify_master_data",
  name: "Classify Master Data",
  description:
    "Classify rows in a master data table by evaluating configurable rules and assigning statuses",
  category: "Transformation",

  inputSchema: z.object({
    updatedAfter: z
      .string()
      .describe(
        "ISO timestamp — only classify rows with updatedAt >= this value. " +
          "Wire from bulk insert's startedAt output. Required to prevent full table scans.",
      ),
    company: z
      .string()
      .optional()
      .describe(
        "Restrict this step to one company code. Wire from the bulk insert " +
          "step's company output so a fanout child touches only its own slice.",
      ),
    division: z
      .string()
      .optional()
      .describe(
        "Restrict this step to one division code. Wire from the bulk insert " +
          "step's division output so a fanout child touches only its own slice.",
      ),
  }),

  outputSchema: z.object({
    processedCount: z.number().describe("Total number of rows evaluated"),
    results: z
      .record(z.string(), z.number())
      .describe(
        'Count of rows per resulting status (e.g., {"staged_internal": 45, "staged": 12})',
      ),
    unchangedCount: z
      .number()
      .describe("Number of rows left unchanged (no rule matched)"),
  }),

  configSchema: z.object({
    targetModel: z
      .string()
      .min(1)
      .describe(
        "Target database table name (must match a registered model, e.g. rexel_e844sd)",
      ),
    sourceStatusFilter: z
      .array(z.string())
      .prefault(["pending"])
      .describe("Only process rows with these statuses"),
    rules: z
      .array(
        z.object({
          name: z.string().describe("Rule name for audit trail"),
          priority: z
            .number()
            .prefault(0)
            .describe(
              "Higher priority rules are evaluated first. First matching rule wins.",
            ),
          conditions: z
            .array(conditionSchema)
            .describe("All conditions must match (AND logic)"),
          conditionGroups: z
            .array(
              z.object({
                logic: z.enum(["AND", "OR"]),
                conditions: z.array(conditionSchema),
              }),
            )
            .optional()
            .describe(
              "Condition groups with configurable AND/OR logic. Takes precedence over flat conditions.",
            ),
          conditionGroupsLogic: z
            .enum(["AND", "OR"])
            .prefault("OR")
            .describe(
              "How condition groups combine. OR = any group matches, AND = all groups must match.",
            ),
          action: z.object({
            setStatus: z
              .string()
              .describe(
                "Freeform status to assign (e.g., 'staged_internal', 'staged')",
              ),
          }),
        }),
      )
      .describe("Rules evaluated in priority order; first match wins per row"),
    defaultAction: z
      .object({
        setStatus: z
          .string()
          .optional()
          .describe(
            "Status to assign if no rules match (omit to leave unchanged)",
          ),
      })
      .optional()
      .describe("Action to apply if no rules match a row"),
    batchSize: z
      .number()
      .prefault(100)
      .describe("Number of rows to update per database transaction"),
    limit: z
      .number()
      .prefault(0)
      .describe("Maximum number of rows to process per run. 0 = unlimited."),
    activityLogging: z
      .object({
        activityModel: z
          .string()
          .describe(
            "MODEL_REGISTRY key for the activity table (e.g. sd_e844d_activities)",
          ),
        foreignKeyField: z
          .string()
          .describe(
            "camelCase FK attribute on the activity model linking to the target row (e.g. sdE844dId)",
          ),
        statusMapping: z
          .record(
            z.string(),
            z.object({
              activityType: z
                .string()
                .describe(
                  "Activity type value (e.g. staged, classified_internal)",
                ),
              description: z
                .string()
                .describe(
                  "Human-readable description for the activity log entry",
                ),
            }),
          )
          .describe("Maps target status values to activity type + description"),
      })
      .optional()
      .describe(
        "Optional activity logging. When present, creates activity records " +
          "for each classified row using the specified model and status-to-type mapping.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
